import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch } from '@/configuration/setup/hooks';
import { sendMessage } from '@/features/app/MessageHandler';
import {
    AppointmentActionKey,
    appointmentItemIdOf,
    ContextResponsePayload,
    VD_CANCEL_APPOINTMENT_REQUEST,
    VD_EDIT_APPOINTMENT_REQUEST,
    VD_MAINTENANCE_TABLE_CONTEXT_REQUEST,
    VD_SCHEDULE_APPOINTMENT_REQUEST,
    WFE_MAINTENANCE_TABLE_CONTEXT_RESPONSE,
} from '@/features/maintenance-data/appointmentMessages';
import { RowSelectionDerived } from '@/features/maintenance-data/hooks/useRowSelection';
import { vehicleDiagnosticsApi } from '@/services/vehicleDiagnosticsApi';

/** How long to wait for WFE's context response before hiding all appointment actions. */
export const CONTEXT_RESPONSE_TIMEOUT_MS = 3000;

export interface UseMaintenanceTableContext {
    /** Appointment actions WFE advertised for the current selection (empty = none / timed out). */
    availableActions: ReadonlySet<AppointmentActionKey>;
    sendSchedule: () => void;
    sendEdit: () => void;
    sendCancel: () => void;
}

/**
 * VD side of the appointment postMessage bridge.
 *
 * - Sends a context request to WFE whenever the selection is appointment-capable (single vehicle,
 *   none solved) so WFE can resolve which appointment actions are allowed.
 * - Listens for WFE's response, exposing `availableActions` and refetching the table when WFE
 *   reports a change (`changed: true`).
 * - If WFE does not answer within {@link CONTEXT_RESPONSE_TIMEOUT_MS}, hides all appointment actions.
 *
 */
export const useMaintenanceTableContext = (selection: RowSelectionDerived): UseMaintenanceTableContext => {
    const dispatch = useAppDispatch();
    const [availableActions, setAvailableActions] = useState<ReadonlySet<AppointmentActionKey>>(new Set());
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    const { selectedCount, selectedVehicleCount, noneSolved, selectedAssetId, selectedItems } = selection;

    // Appointment-capable: a single vehicle with nothing solved yet.
    const isAppointmentCapable = selectedCount > 0 && selectedVehicleCount === 1 && noneSolved && !!selectedAssetId;

    const contextRequestItems = useMemo(
        () =>
            selectedItems.map((item) => ({
                itemId: appointmentItemIdOf(item),
                itemType: item.component_type,
                ...(item.appointment?.id ? { appointmentId: item.appointment.id } : {}),
            })),
        [selectedItems],
    );

    const appointmentId = useMemo(
        () => selectedItems.find((item) => item.appointment?.id)?.appointment?.id,
        [selectedItems],
    );

    const clearTimer = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        }
    };

    // Request available actions from WFE whenever the eligible selection changes.
    useEffect(() => {
        clearTimer();
        if (!isAppointmentCapable || !selectedAssetId) {
            setAvailableActions(new Set());
            return;
        }

        sendMessage({
            type: VD_MAINTENANCE_TABLE_CONTEXT_REQUEST,
            payload: { assetId: selectedAssetId, items: contextRequestItems },
        });

        // Graceful timeout: hide all appointment actions if WFE never answers.
        timeoutRef.current = setTimeout(() => setAvailableActions(new Set()), CONTEXT_RESPONSE_TIMEOUT_MS);

        return clearTimer;
    }, [isAppointmentCapable, selectedAssetId, contextRequestItems]);

    // Listen for WFE's response.
    useEffect(() => {
        const listener = (event: MessageEvent) => {
            const { type, payload } = (event.data ?? {}) as { type?: string; payload?: ContextResponsePayload };
            if (type !== WFE_MAINTENANCE_TABLE_CONTEXT_RESPONSE || !payload) {
                return;
            }
            clearTimer();
            setAvailableActions(new Set(payload.availableActions ?? []));
            if (payload.changed) {
                dispatch(vehicleDiagnosticsApi.util.invalidateTags(['MaintenanceData']));
            }
        };
        window.addEventListener('message', listener, false);
        return () => window.removeEventListener('message', listener, false);
    }, [dispatch]);

    const sendSchedule = useCallback(() => {
        if (!selectedAssetId) {
            return;
        }
        sendMessage({
            type: VD_SCHEDULE_APPOINTMENT_REQUEST,
            payload: {
                assetId: selectedAssetId,
                items: selectedItems.map((item) => ({
                    itemId: appointmentItemIdOf(item),
                    itemType: item.component_type,
                })),
            },
        });
    }, [selectedAssetId, selectedItems]);

    const sendEdit = useCallback(() => {
        if (!appointmentId) {
            return;
        }
        sendMessage({ type: VD_EDIT_APPOINTMENT_REQUEST, payload: { appointmentId } });
    }, [appointmentId]);

    const sendCancel = useCallback(() => {
        if (!appointmentId) {
            return;
        }
        sendMessage({ type: VD_CANCEL_APPOINTMENT_REQUEST, payload: { appointmentId } });
    }, [appointmentId]);

    return { availableActions, sendSchedule, sendEdit, sendCancel };
};
